import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import { TrashIcon } from '@heroicons/react/24/outline';
import {
  AE_BTN_SECONDARY,
  AE_CARD_COMPACT,
  AE_ICON_SIZE,
  AE_INPUT_COMPACT,
  AE_LABEL_COMPACT,
  AE_MUTED,
  AE_SECTION_TITLE_COMPACT,
} from '@/components/assessment-engine/layout/aeTheme';
import { AeIconButton } from '@/components/assessment-engine/layout';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import type { AeCampaignAdmin, AeCampaignAdminCandidate } from '@/types/assessmentEngine';
import { getAssessmentApiErrorMessage } from '@/components/assessment-engine/campaigns/campaignListUtils';

interface CampaignAdminsPanelProps {
  campaignId: string;
}

/** Manage TMOS users who administer this campaign and receive completion emails. */
export const CampaignAdminsPanel: React.FC<CampaignAdminsPanelProps> = ({ campaignId }) => {
  const { t } = useLanguage();
  const [admins, setAdmins] = useState<AeCampaignAdmin[]>([]);
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState<AeCampaignAdminCandidate[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    assessmentEngineService
      .listCampaignAdmins(campaignId)
      .then(setAdmins)
      .catch(() => toast.error(t('assessmentEngine.admins.failedLoad')));
  }, [campaignId, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const q = search.trim();
    // Do not preload users — lists can be huge; only search once the admin types
    if (!q) {
      setCandidates([]);
      return;
    }
    const timer = window.setTimeout(() => {
      assessmentEngineService
        .listCampaignAdminCandidates(campaignId, q)
        .then(setCandidates)
        .catch(() => setCandidates([]));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [campaignId, search]);

  const addAdmin = async (userId: string) => {
    setBusy(true);
    try {
      await assessmentEngineService.addCampaignAdmin(campaignId, userId);
      toast.success(t('assessmentEngine.admins.added'));
      setSearch('');
      load();
    } catch (error) {
      toast.error(getAssessmentApiErrorMessage(error, t('assessmentEngine.admins.addFailed')));
    } finally {
      setBusy(false);
    }
  };

  const removeAdmin = async (userId: string, name: string) => {
    if (!window.confirm(t('assessmentEngine.admins.removeConfirm', { name }))) return;
    setBusy(true);
    try {
      await assessmentEngineService.removeCampaignAdmin(campaignId, userId);
      toast.success(t('assessmentEngine.admins.removed'));
      load();
    } catch (error) {
      toast.error(getAssessmentApiErrorMessage(error, t('assessmentEngine.admins.removeFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${AE_CARD_COMPACT} space-y-3`}>
      <div>
        <h2 className={AE_SECTION_TITLE_COMPACT}>{t('assessmentEngine.admins.title')}</h2>
        <p className={`${AE_MUTED} text-xs`}>{t('assessmentEngine.admins.hint')}</p>
        <p className={`${AE_MUTED} text-xs`}>
          {t('assessmentEngine.admins.digestSetupNote')}
        </p>
      </div>

      <ul className="space-y-1">
        {admins.map((admin) => {
          const name = `${admin.first_name} ${admin.last_name}`.trim() || admin.email;
          return (
            <li
              key={admin.user_id}
              className="flex items-center justify-between gap-2 border-b border-[var(--color-border)] py-1.5"
            >
              <div>
                <p className="text-sm text-[var(--color-surface-text)]">
                  {name}
                  {admin.is_primary && (
                    <span className="ml-2 text-xs text-[var(--color-secondary-text)]">
                      ({t('assessmentEngine.admins.primary')})
                    </span>
                  )}
                </p>
                <p className="text-xs text-[var(--color-secondary-text)]">{admin.email}</p>
              </div>
              {!admin.is_primary && (
                <AeIconButton
                  compact
                  label={t('assessmentEngine.admins.remove')}
                  variant="danger"
                  disabled={busy}
                  onClick={() => void removeAdmin(admin.user_id, name)}
                >
                  <TrashIcon className={AE_ICON_SIZE} aria-hidden />
                </AeIconButton>
              )}
            </li>
          );
        })}
        {admins.length === 0 && (
          <li className={AE_MUTED}>{t('assessmentEngine.admins.empty')}</li>
        )}
      </ul>

      <div className="max-w-md">
        <label className={AE_LABEL_COMPACT} htmlFor="ae-admin-search">
          {t('assessmentEngine.admins.addLabel')}
        </label>
        <input
          id="ae-admin-search"
          className={AE_INPUT_COMPACT}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('assessmentEngine.admins.searchPlaceholder')}
        />
        {candidates.length > 0 && (
          <ul className="mt-1.5 max-h-40 overflow-y-auto border border-[var(--color-border)] rounded divide-y divide-[var(--color-border)]">
            {candidates.map((c) => {
              const name = `${c.first_name} ${c.last_name}`.trim() || c.email;
              return (
                <li key={c.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5">
                  <div>
                    <p className="text-sm text-[var(--color-surface-text)]">{name}</p>
                    <p className="text-xs text-[var(--color-secondary-text)]">{c.email}</p>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    className={AE_BTN_SECONDARY}
                    onClick={() => void addAdmin(c.id)}
                  >
                    {t('assessmentEngine.admins.add')}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {search.trim() && candidates.length === 0 && (
          <p className={`${AE_MUTED} mt-1.5 text-xs`}>{t('assessmentEngine.admins.noCandidates')}</p>
        )}
      </div>
    </div>
  );
};
